import { getSuggestionsDbApi } from 'api/indexed_db/suggestions';
import { SuggestionField } from 'indexed_db/suggestionsDB';
import { useCallback, useEffect, useState } from 'react';
import create from 'zustand';
import { mergeDeepRight } from 'rambda';
import { captureException } from '@sentry/react';
import { useOnlineStatus } from './useOnlineStatus';

export interface SuggestionsStore {
  suggestions?: Partial<Record<SuggestionField, string[] | undefined>>;
  getFieldSuggestions: (field: SuggestionField) => string[] | undefined;
  setFieldSuggestions: (field: SuggestionField, payload: string[]) => void;
}

const useSuggestionsStore = create<SuggestionsStore>((set, get) => ({
  suggestions: {},
  getFieldSuggestions: (field: SuggestionField): string[] | undefined => get().suggestions?.[field],
  setFieldSuggestions: (field: SuggestionField, payload: string[]) =>
    set(state => mergeDeepRight(state, { suggestions: { [field]: payload } })),
}));

export interface SuggestionsShape {
  suggestions: string[];
  isLoading: boolean;
}

const getSuggestionData = async (isOnline: boolean, field: SuggestionField, fetchData: () => Promise<string[]>) => {
  const suggestionDb = getSuggestionsDbApi();

  if (isOnline) {
    const data = await fetchData();
    suggestionDb.setFieldSuggestions(field, data);
  }

  return await suggestionDb.getFieldSuggestions(field);
};

export const useSuggestions = (
  field: SuggestionField,
  fetchDataCallback: () => Promise<string[]>,
): SuggestionsShape => {
  const [isLoading, setIsLoading] = useState(false);
  const { getFieldSuggestions, setFieldSuggestions } = useSuggestionsStore();
  const { isOnline } = useOnlineStatus();

  const getSuggestions = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await getSuggestionData(isOnline, field, fetchDataCallback);
      setFieldSuggestions(field, data);
    } catch (error) {
      console.error(error);
      captureException(error);
    } finally {
      setIsLoading(false);
    }
  }, [setIsLoading, setFieldSuggestions, field, isOnline, fetchDataCallback]);

  useEffect(() => {
    const fieldSuggestions = getFieldSuggestions(field);
    if (fieldSuggestions) return;

    getSuggestions();
  }, [field, getFieldSuggestions, getSuggestions]);

  return {
    suggestions: getFieldSuggestions(field) ?? [],
    isLoading,
  };
};
